import { useQueryClient } from '@tanstack/react-query';
import { tx } from '../i18n';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { mensajeDe } from '../lib/api';
import type { Tono } from '../lib/appliance';
import { Modal } from './Modal';

/** El encabezado de cada pantalla: qué es, para qué sirve y su acción principal. */
export function Cabecera({ titulo, children, accion }: { titulo: string; children?: ReactNode; accion?: ReactNode }) {
  return (
    <header className="cab">
      <div>
        <h1>{titulo}</h1>
        {children && <p>{children}</p>}
      </div>
      {accion && <div className="cab-acc">{accion}</div>}
    </header>
  );
}

/** Vuelve a pedir todo lo que empiece con estas rutas (la lista y lo que depende de ella). */
export function useRefrescar() {
  const qc = useQueryClient();
  return useCallback(
    (...rutas: string[]) =>
      qc.invalidateQueries({
        predicate: (q) => typeof q.queryKey[0] === 'string' && rutas.some((r) => (q.queryKey[0] as string).startsWith(r)),
      }),
    [qc],
  );
}

/** El aviso de la pantalla ("Guardado", "No se pudo…"): arriba de todo, se va solo. */
export function useAviso(): [ReactNode, (texto: string, error?: boolean) => void] {
  const [aviso, setAviso] = useState<{ texto: string; error: boolean; n: number } | null>(null);
  useEffect(() => {
    if (!aviso) return;
    const t = setTimeout(() => setAviso(null), aviso.error ? 8000 : 4000);
    return () => clearTimeout(t);
  }, [aviso]);
  const avisar = useCallback((texto: string, error = false) => setAviso((a) => ({ texto, error, n: (a?.n ?? 0) + 1 })), []);
  const nodo = aviso ? (
    <p key={aviso.n} className={`aviso${aviso.error ? ' error' : ''}`} role={aviso.error ? 'alert' : 'status'}>
      {aviso.texto}
    </p>
  ) : null;
  return [nodo, avisar];
}

/** Corre un cambio (un botón de la lista) y avisa cómo le fue, sin dejar nada a medias. */
export function useCambio(avisar: (t: string, e?: boolean) => void, refrescar: () => unknown) {
  return useCallback(
    async (f: () => Promise<unknown>, ok?: string) => {
      try {
        await f();
        if (ok) avisar(ok);
      } catch (e) {
        avisar(mensajeDe(e), true);
      } finally {
        void refrescar();
      }
    },
    [avisar, refrescar],
  );
}

/**
 * Lo que se ve mientras una consulta carga, falla o no trae nada. Nunca una pantalla en
 * blanco: o está cargando, o dice qué pasó y deja reintentar, o dice que no hay nada.
 *
 * El texto de `vacio` se muestra **solo si de verdad no hay nada** (lista vacía o sin
 * datos): antes bastaba con pasarlo para que tapara el contenido, y una pantalla con datos
 * terminaba mostrando "no hay nada".
 */
export function Consulta({
  q,
  vacio,
  children,
}: {
  q: { isPending: boolean; isError: boolean; error: unknown; refetch: () => unknown; data?: unknown };
  vacio?: ReactNode;
  children: ReactNode;
}) {
  if (q.isPending)
    return (
      <p className="cargando" role="status">
        {tx.comun.cargando}
      </p>
    );
  if (q.isError)
    return (
      <div className="falla" role="alert">
        <p>{mensajeDe(q.error)}</p>
        <button type="button" className="btn chico" onClick={() => q.refetch()}>
          {tx.comun.volverAIntentar}
        </button>
      </div>
    );
  const sinNada = Array.isArray(q.data) ? q.data.length === 0 : q.data === null || q.data === undefined;
  if (vacio && sinNada) return <>{vacio}</>;
  return <>{children}</>;
}

export function Vacio({ children }: { children: ReactNode }) {
  return <p className="vacio">{children}</p>;
}

/** Una insignia de estado: el color es la información, el texto la confirma. */
export function Pastilla({ tono, children }: { tono: Tono; children: ReactNode }) {
  return <span className={`pill ${tono}`}>{children}</span>;
}

/**
 * Un formulario en ventana. `alEnviar` devuelve un texto si algo falta (se valida antes de
 * pedirle nada a la API) o tira el error de la API, que se muestra tal cual.
 */
export function Ventana({
  titulo,
  boton,
  peligro = false,
  alCerrar,
  alEnviar,
  children,
}: {
  titulo: string;
  boton: string;
  peligro?: boolean;
  alCerrar: () => void;
  alEnviar: () => Promise<string | void>;
  children: ReactNode;
}) {
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);
  const enviar = async () => {
    setError('');
    setEnviando(true);
    try {
      const falta = await alEnviar();
      if (falta) setError(falta);
    } catch (e) {
      setError(mensajeDe(e));
    }
    setEnviando(false);
  };
  return (
    <Modal
      titulo={titulo}
      alCerrar={alCerrar}
      pie={
        <>
          <button type="button" className="btn fantasma" onClick={alCerrar}>
            {tx.comun.cancelar}
          </button>
          <button type="button" className={`btn ${peligro ? 'peligro' : 'prim'}`} onClick={() => void enviar()} disabled={enviando}>
            {enviando ? 'One moment…' : boton}
          </button>
        </>
      }
    >
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          void enviar();
        }}
      >
        {children}
        {error && (
          <p className="error-form" role="alert">
            {error}
          </p>
        )}
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}

/** Algo que se ve una sola vez (el código de alta): se copia con un toque. */
export function Secreto({ valor, etiqueta }: { valor: string; etiqueta: string }) {
  const [copiado, setCopiado] = useState(false);
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(valor);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      // sin portapapeles (página sin https): queda seleccionado para copiarlo a mano
      const el = document.getElementById('secreto-valor');
      if (el) window.getSelection()?.selectAllChildren(el);
    }
  };
  return (
    <div className="secreto">
      <small>{etiqueta}</small>
      <code id="secreto-valor">{valor}</code>
      <button type="button" className="btn chico" onClick={() => void copiar()}>
        {copiado ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

/** Lo que falta en un formulario, en el orden en que se ve: se le dice lo primero. */
export const primero = (...xs: (string | false | null | undefined)[]) => xs.find((x): x is string => !!x) ?? '';

/**
 * Una tabla que se entiende también en un teléfono. En angosto las tablas se apilan y los
 * encabezados se esconden, así que las cifras quedaban sueltas: "Hotel Norte, 1, 98, 2" sin
 * decir qué era cada una. Acá cada celda se lleva su rótulo copiado de la columna, y el CSS
 * lo muestra solo cuando la tabla está apilada.
 *
 * Se copia al pintar y no a mano celda por celda: así vale para las ocho tablas del panel, y
 * al cambiar de idioma los rótulos cambian con la tabla.
 */
export function Tabla({ children, className = '' }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLTableElement>(null);
  useEffect(() => {
    const tabla = ref.current;
    if (!tabla) return;
    const rotulos = [...tabla.querySelectorAll('thead th')].map((th) => th.textContent?.trim() ?? '');
    for (const fila of tabla.querySelectorAll('tbody tr')) {
      [...fila.children].forEach((celda, i) => {
        // La primera columna es el nombre de la fila: no necesita rótulo, se lee sola.
        if (i > 0 && rotulos[i]) celda.setAttribute('data-rotulo', rotulos[i]);
      });
    }
  });
  return (
    <table ref={ref} className={`tabla ${className}`.trim()}>
      {children}
    </table>
  );
}
